import $ from 'jquery';
import Deferred from './deferred-promise.js';

export const MILLISECONDS_MULTIPLIER = 1000;
export const ANIMATION_END = 'animationend';
export const TRANSITION_END = 'transitionend';

/**
 * Trick to restart an element's animation
 *
 * @param {HTMLElement} element
 * @return void
 *
 * @see https://www.charistheo.io/blog/2021/02/restart-a-css-animation-with-javascript/#restarting-a-css-animation
 * @see https://stackoverflow.com/a/21665117/3759615
 */
export const reflow = (element) => {
	return void (element.offsetHeight);
};

/**
 * @alias reflow
 * @param {HTMLElement} element
 */
export const repaint = (element) => {
	return void (element.offsetWidth);
};

/**
 * @param {HTMLElement} element
 */
export const triggerAnimationEnd = (element) => {
	element.dispatchEvent(new Event(ANIMATION_END));
};

/**
 * @param {HTMLElement} element
 */
export const triggerTransitionEnd = (element) => {
	element.dispatchEvent(new Event(TRANSITION_END));
};

/**
 * @param {'animation'|'transition'} type
 * @param {HTMLElement} element
 */
export const getDurationFromElement = (type, element) => {
	if (!element) {
		return 0;
	}

	// Get transition/animation duration of the element
	// @ts-ignore
	let { [type + 'Duration']: duration, [type + 'Delay']: delay } = window.getComputedStyle(element);

	const floatDuration = Number.parseFloat(duration);
	const floatDelay = Number.parseFloat(delay);

	// Return 0 if element or transition duration is not found
	if (!floatDuration && !floatDelay) {
		return 0;
	}

	// If multiple durations are defined, take the first
	duration = duration.split(',')[0];
	delay = delay.split(',')[0];

	return (Number.parseFloat(duration) + Number.parseFloat(delay)) * MILLISECONDS_MULTIPLIER;
};

/**
 * @param {{
 *  element?: HTMLElement;
 *  classList?: string|string[],
 *  type?: 'animation'|'transition';
 *  mode?: 'add_remove'|'remove_add'|'add'|'remove'|'none';
 *  waitForEnd?: boolean;
 *  duration?: number;
 *  delay?: number;
 * }} options
 */
export const animController = (options = {}) => {

	let {
		element,
		classList = null,
		type = 'transition',
		mode = 'none',
		waitForEnd = true,
		duration,
		delay,
	} = options;

	if (!element) {
		throw new Error('Missing element in animController');
	}

	return Deferred.create((def) => {

		if (classList) {
			if (typeof classList === 'string') {
				classList = [classList];
			}
		}
		else {
			classList = [];
		}

		if (classList.length === 0) {
			mode = 'none';
		}

		const changedProps = {};

		// Force animation duration to zero
		if (!waitForEnd) {
			for (const prop of [`--${type}-duration`, `${type}-duration`]) {
				changedProps[prop] = element.style.getPropertyValue(prop);
				element.style.setProperty(prop, '0s');
			}
		}
		// Allow setting custom duration/delay
		else {
			// Add duration
			if (typeof duration === 'number') {
				for (const prop of [`--${type}-duration`, `${type}-duration`]) {
					changedProps[prop] = element.style.getPropertyValue(prop);
					element.style.setProperty(prop, duration + 'ms');
				}
			}

			// Add delay
			if (typeof delay === 'number') {
				for (const prop of [`--${type}-delay`, `${type}-delay`]) {
					changedProps[prop] = element.style.getPropertyValue(prop);
					element.style.setProperty(prop, delay + 'ms');
				}
			}
		}

		let reflowDone = false;

		// Make sure animations start
		// Note: some animations/transitions require reflowing before adding the animation/transition class
		if (mode === 'add' || mode === 'remove') {
			reflowDone = true;
			reflow(element);
		}

		// Add/Remove animation classes
		switch (mode) {
			case 'add_remove':
			case 'add': {
				element.classList.add(...classList);
				break;
			}
			case 'remove_add':
			case 'remove': {
				element.classList.remove(...classList);
				break;
			}
		}

		// Make sure animations start
		// Note: most animations/transitions require reflowing after adding the animation/transition class
		if (mode !== 'none' && reflowDone === false) {
			reflow(element);
		}

		const durationPadding = 5;
		const emulatedDuration = waitForEnd ? (getDurationFromElement(type, element) + durationPadding) : 0;

		let called = false;
		let timer;
		const eventName = type === 'animation' ? ANIMATION_END : TRANSITION_END;
		const triggerEndFn = type === 'animation' ? triggerAnimationEnd : triggerTransitionEnd;

		const handler = ({ target }) => {
			if (target !== element) {
				return;
			}
			def.resolve(element);
		};

		// On deferred fulfilled
		def.always(() => {
			// Remove timer & event handler
			called = true;
			timer && window.clearTimeout(timer);
			element.removeEventListener(eventName, handler);

			// Remove animation classes
			if (mode) {
				// Add/Remove animation classes
				switch (mode) {
					case 'add_remove': {
						element.classList.remove(...classList);
						break;
					}
					case 'remove_add': {
						element.classList.add(...classList);
						break;
					}
				}
			}

			// Set back values for changed props
			for (const [name, value] of Object.entries(changedProps)) {
				element.style.setProperty(name, value);
			}
		});

		// Start timer & event
		element.addEventListener(eventName, handler);
		timer = window.setTimeout(() => {
			timer = null;
			if (!called) {
				triggerEndFn(element);
			}
		}, emulatedDuration);
	});
};

/**
 * @param {{
 *  element?: HTMLElement;
 *  classList?: string|string[],
 *  type?: 'animation'|'transition';
 *  mode?: 'add_remove'|'remove_add'|'add'|'remove'|'none';
 *  waitForEnd?: boolean;
 *  duration?: number;
 *  delay?: number;
 * }} options
 */
export const animControllerDeferred = (options = {}) => {

	let {
		element,
		classList = null,
		type = 'transition',
		mode = 'none',
		waitForEnd = true,
		duration,
		delay,
	} = options;

	if (!element) {
		throw new Error('Missing element in animController');
	}

	// Immediate resolve the active animation
	clearDeferredAnimations(element);

	return $.Deferred((df) => {

		// Save the resolve function
		element['__df_anim__'] = df;

		if (classList) {
			if (typeof classList === 'string') {
				classList = [classList];
			}
		}
		else {
			classList = [];
		}

		if (classList.length === 0) {
			mode = 'none';
		}

		const changedProps = {};

		// Force animation duration to zero
		if (!waitForEnd) {
			for (const prop of [`--${type}-duration`, `${type}-duration`]) {
				// Save original value
				changedProps[prop] = element.style.getPropertyValue(prop);
				element.style.setProperty(prop, '0s');
			}
		}
		// Allow setting custom duration/delay
		else {
			// Add duration
			if (typeof duration === 'number') {
				for (const prop of [`--${type}-duration`, `${type}-duration`]) {
					changedProps[prop] = element.style.getPropertyValue(prop);
					element.style.setProperty(prop, duration + 'ms');
				}
			}

			// Add delay
			if (typeof delay === 'number') {
				for (const prop of [`--${type}-delay`, `${type}-delay`]) {
					changedProps[prop] = element.style.getPropertyValue(prop);
					element.style.setProperty(prop, delay + 'ms');
				}
			}
		}

		let reflowDone = false;

		// Make sure animations start
		// Note: some animations/transitions require reflowing before adding the animation/transition class
		if (mode === 'add' || mode === 'remove') {
			if (element?.getAnimations?.()?.filter(a => a.playState === 'running')?.length > 0) {
				reflowDone = true;
				reflow(element);
			}
		}

		// Add/Remove animation classes
		switch (mode) {
			case 'add_remove':
			case 'add': {
				element.classList.add(...classList);
				break;
			}
			case 'remove_add':
			case 'remove': {
				element.classList.remove(...classList);
				break;
			}
		}

		// Make sure animations start
		// Note: most animations/transitions require reflowing after adding the animation/transition class
		if (mode !== 'none' && reflowDone === false) {
			reflow(element);
		}

		const durationPadding = 5;
		const emulatedDuration = waitForEnd ? (getDurationFromElement(type, element) + durationPadding) : 0;
		const triggerEndFn = type === 'animation' ? triggerAnimationEnd : triggerTransitionEnd;

		let called = false;
		let timer;
		const eventName = type === 'animation' ? ANIMATION_END : TRANSITION_END;

		const handler = ({ target }) => {
			if (target !== element) {
				return;
			}
			df.resolve(element);
		};

		df.always((_, isForced = false) => {

			// Remove timer & event handler
			element['__df_anim__'] = null;
			called = true;
			timer && window.clearTimeout(timer);
			element.removeEventListener(eventName, handler);

			// Remove animation classes
			if (mode) {
				// Add/Remove animation classes
				switch (mode) {
					case 'add_remove': {
						element.classList.remove(...classList);
						break;
					}
					case 'remove_add': {
						element.classList.add(...classList);
						break;
					}
				}

				// Must reflow to cancel animations
				if (isForced === true) {
					reflow(element);
				}
			}

			// Set back values for changed props
			for (const [name, value] of Object.entries(changedProps)) {
				element.style.setProperty(name, value);
			}
		});

		// Start timer & event
		element.addEventListener(eventName, handler);
		timer = window.setTimeout(() => {
			timer = null;
			if (!called) {
				triggerEndFn(element);
			}
		}, emulatedDuration);
	});
};

/**
 * @param {HTMLElement} element
 * @param {string} animation
 * @param {string|string[]} modifierList
 * @param {string|{ prefix?: string; waitForEnd?: boolean; delay?: number; duration?: number; }} prefixOrOptions
 * @param {boolean=} waitForEnd
 */
export const cssAnimation = (element, animation, modifierList = [], prefixOrOptions = '', waitForEnd = true) => {

	// Convert to array
	if (typeof modifierList === 'string') {
		modifierList = [modifierList];
	}

	let prefix = '';
	let delay;
	let duration;

	// Prefix provided as string
	if (typeof prefixOrOptions === 'string') {
		prefix = prefixOrOptions;
	}
	// Get options from object
	else if (prefixOrOptions) {
		({
			waitForEnd = waitForEnd,
			prefix = '',
			delay,
			duration,
		} = prefixOrOptions);
	}

	const animationName = `${prefix}${animation}`;
	const classList = [`${prefix}animated`, animationName, ...modifierList.map(v => `${prefix}${v}`)];

	// Control the whole start & end of the animation
	return animController({
		element,
		classList,
		type: 'animation',
		mode: 'add_remove',
		waitForEnd,
		delay,
		duration,
	});
};

/**
 * @param {HTMLElement} element
 * @param {string} animation
 * @param {string|string[]} modifierList
 * @param {string|{ prefix?: string; waitForEnd?: boolean; delay?: number; duration?: number; }} prefixOrOptions
 * @param {boolean=} waitForEnd
 */
export const cssAnimation2 = (element, animation, modifierList = [], prefixOrOptions = '', waitForEnd = true) => {

	// Convert to array
	if (typeof modifierList === 'string') {
		modifierList = [modifierList];
	}

	let prefix = '';
	let delay;
	let duration;

	// Prefix provided as string
	if (typeof prefixOrOptions === 'string') {
		prefix = prefixOrOptions;
	}
	// Get options from object
	else if (prefixOrOptions) {
		({
			waitForEnd = waitForEnd,
			prefix = '',
			delay,
			duration,
		} = prefixOrOptions);
	}

	const animationName = `${prefix}${animation}`;
	const classList = [`${prefix}animated`, animationName, ...modifierList.map(v => `${prefix}${v}`)];

	// Control the whole start & end of the animation
	return animControllerDeferred({
		element,
		classList,
		type: 'animation',
		mode: 'add_remove',
		waitForEnd,
		delay,
		duration,
	});
};

/**
 * @param {JQuery | HTMLElement} $element
 */
export const clearDeferredAnimations = ($element) => {
	if ($element) {
		const element = $element instanceof $ ? $element[0] : $element;
		if (element['__df_anim__']) {
			element['__df_anim__'].resolve(element, true);
		}
	}
};

/**
 * @param {JQuery} $element
 * @param {string} filterVal
 */
const setFilter = ($element, filterVal) => {
	$element.css({
		'-webkit-filter': filterVal,
		'filter': filterVal,
	});
};

const defaultFilterValues = {
	blur: 0,
	brightness: 1,
	contrast: 1,
	// 'drop-shadow':
	grayscale: 0,
	'hue-rotate': 0,
	invert: 0,
	opacity: 1,
	saturate: 1,
	sepia: 0,
};

/**
 *
 * @param {JQuery} $element
 * @param {*} opt
 * @param {string[]=} filterNames
 * @param {boolean=} resetVals
 */
const updateAnim = ($element, opt, filterNames, resetVals = false) => {
	// let finalProps;

	return {
		start(/* anim */) {
			// finalProps = anim.originalProperties;
			setFilter($element, filterNames.map(v => `${v}(${this['fake_' + v] || defaultFilterValues[v]})`).join(' '));
		},
		// step: function (now, fx) {
		// 	console.log('step', this);
		// 	debugger;
		// },
		progress(/* anim, progress, remaining */) {
			// console.log('progress', this);
			// debugger;
			// console.log('progress: ', filterNames.map(v => `${v}(${this['fake_' + v]})`).join(' '));
			setFilter($element, filterNames.map(v => `${v}(${this['fake_' + v]})`).join(' '));
		},
		complete() {
			if (resetVals) {
				setFilter($element, '');
				for (const prop of filterNames) {
					$element.removeProp('fake_' + prop);
				}
			}
			else {
				setFilter($element, filterNames.map(v => `${v}(${this['fake_' + v]})`).join(' '));
			}
		},
		...opt,
	};
};

/**
 * @param {JQuery} $element
 * @param {{ [key: string]: number }} filters
 * @param {{ [key: string]: number|string }} startOptions
 * @param {{ [key: string]: number|string }=} endOptions
 */
export const animateFilterJQ = ($element, filters, startOptions, endOptions) => {
	const filterNames = [];
	const fakeFiltersStart = {};
	const fakeFiltersEnd = {};

	for (const [name, val] of Object.entries(filters)) {
		const fakeName = 'fake_' + name;
		filterNames.push(name);
		fakeFiltersStart[fakeName] = defaultFilterValues[name];
		fakeFiltersEnd[fakeName] = val;
	}

	// Make sure no animation is running
	$element.finish();
	// Make sure all init values are set
	$element.prop(fakeFiltersStart);

	// Start animation
	$element.animate(fakeFiltersEnd, updateAnim($element, startOptions, filterNames));

	// End animation
	if (endOptions) {
		$element.animate(fakeFiltersStart, updateAnim($element, endOptions, filterNames, true));
	}
};

// Legacy exports
mpro.cssAnimation = cssAnimation;

export default mpro;